import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import SecondaryButton from '../../../components/secondaryButton';
import './uddannelsesside.css';

// Funktion til at håndtere både markdown og HTML-indhold
const prepareContent = (content) => {
  if (!content) return '';

  // Hvis teksten allerede er HTML (fra rich text editor)
  if (content.includes('<p>') || content.includes('<h') || content.includes('<ul>')) {
    // Vi beholder HTML som den er, da formatering håndteres korrekt i rich text editor
    return content;
  }

  // Hvis det er markdown eller plain text
  // Konverter overskrifter
  let html = content.replace(/^## (.*?)$/gm, '<h3>$1</h3>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  
  // Del teksten på dobbelte linjeskift, hvilket normalt indikerer nye afsnit
  const paragraphs = html.split(/\n\s*\n/); // Split ved tomme linjer
  html = paragraphs.map(p => {
    const trimmedP = p.trim();
    if (trimmedP === '') return '';
    
    // Håndter lister
    if (trimmedP.includes('<li>')) {
      return `<ul>${trimmedP}</ul>`;
    }
    
    // Håndter overskrifter
    if (trimmedP.includes('<h3>')) {
      return trimmedP;
    }
    
    // Almindelige afsnit - bevar enkelte linjeskift som mellemrum
    return `<p>${trimmedP.replace(/\n/g, ' ')}</p>`;
  }).join('\n\n'); // Tilføj afstand mellem afsnit

  return html;
};

const UddannelseSide = () => {
  const { id } = useParams();
  const [uddannelseData, setUddannelseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUddannelseData = async () => {
      try {
        // Hent alle uddannelser
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists() && uddannelserDoc.data().uddannelser) {
          const uddannelser = uddannelserDoc.data().uddannelser;
          const uddannelse = uddannelser.find(u => u.id === id);
          
          if (uddannelse) {
            setUddannelseData(uddannelse);
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af uddannelsesdata:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchUddannelseData();
    }
  }, [id]);

  if (isLoading) {
    return <div className="uddannelsesside-loading">Indlæser...</div>;
  }

  if (!uddannelseData) {
    return (
      <div className="uddannelsesside-not-found">
        <h1>Uddannelse ikke fundet</h1>
        <p>Vi kunne ikke finde den uddannelse, du leder efter.</p>
        <a href="/uddannelse">Tilbage til Uddannelser</a>
      </div>
    );
  }

  return (
    <div className="uddannelsesside">
      <div className="uddannelsesside-container">
        <h1 className="uddannelsesside-title">{uddannelseData.title}</h1>
        
        {uddannelseData.imageUrl && (
          <div className="uddannelsesside-image">
            <img src={uddannelseData.imageUrl} alt={uddannelseData.title} />
          </div>
        )}
        
        <div className="uddannelsesside-content">
          {uddannelseData.fullDescription && (
            <div dangerouslySetInnerHTML={{ __html: prepareContent(uddannelseData.fullDescription) }} />
          )}
          
          {!uddannelseData.fullDescription && uddannelseData.textBefore && (
            <div className="beskrivelse-top" dangerouslySetInnerHTML={{ __html: prepareContent(uddannelseData.textBefore) }} />
          )}
          
          {!uddannelseData.fullDescription && !uddannelseData.textBefore && uddannelseData.description && (
            <p>{uddannelseData.description}</p>
          )}
        </div>
        
        <div className="uddannelsesside-events">
          <h2 className="uddannelsesside-events-title">Events</h2>
          <div className="uddannelsesside-events-grid">
            {uddannelseData.events && uddannelseData.events.map(event => (
              <div className="uddannelsesside-event-card" key={event.id}>
                {event.imageUrl && (
                  <div className="uddannelsesside-event-image">
                    <img src={event.imageUrl} alt={event.title} />
                  </div>
                )}
                <div className="uddannelsesside-event-content">
                  <h3>{event.title}</h3>
                  <div 
                    className="uddannelsesside-event-description"
                    dangerouslySetInnerHTML={{ __html: prepareContent(event.description) }} 
                  />
                  {event.externalLink && (
                    <SecondaryButton href={event.externalLink}>
                      {event.buttonText || 'Tilmeld'}
                    </SecondaryButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {!uddannelseData.fullDescription && uddannelseData.textAfter && (
          <div className="uddannelsesside-content">
            <div className="beskrivelse-bottom" dangerouslySetInnerHTML={{ __html: prepareContent(uddannelseData.textAfter) }} />
          </div>
        )}
        
        <div className="uddannelsesside-back">
          <a href="/uddannelse">Tilbage til oversigten</a>
        </div>
      </div>
    </div>
  );
};

export default UddannelseSide; 